/**
 * RBAC-3.4 · FR-2a, FR-2b. Preview, adopt, check and reverse an adoption.
 *
 * ⚠️ THIS MODULE HOLDS NO RULES. Who moves, who stays, which roles are adoptable and when a reversal
 * is refused are all `20261101c6`. What the answers mean, and what is sent back as confirmation, is
 * `lib/roleAdoption.ts`.
 */

import { supabase } from '@/lib/supabaseClient';
import { duplicateRoleErrorMessage, type RpcErrorLike } from '@/lib/roleDuplication';
import {
  ADOPT_ROLE_RPC,
  REVERT_ROLE_ADOPTION_RPC,
  ROLE_ADOPTION_PREVIEW_RPC,
  ROLE_ADOPTION_STATUS_RPC,
  adoptRoleArgs,
  parseAdoptResult,
  parseAdoptionPreview,
  parseAdoptionStatus,
  parseRevertResult,
  type AdoptRoleResult,
  type RevertAdoptionResult,
  type RoleAdoptionPreview,
  type RoleAdoptionStatus,
} from '@/lib/roleAdoption';

export class RoleAdoptionError extends Error {
  readonly code: string | null;
  constructor(err: RpcErrorLike | null, fallback = 'The role could not be adopted.') {
    super(err?.message?.trim() ? duplicateRoleErrorMessage(err) : fallback);
    this.name = 'RoleAdoptionError';
    this.code = err?.code ?? null;
  }
}

const unreadable = (what: string) => new RoleAdoptionError({ message: `${what} came back in a shape this screen does not understand. Reload to see the current state.` });

export async function fetchRoleAdoptionPreview(sourceRoleId: number): Promise<RoleAdoptionPreview> {
  const { data, error } = await supabase.rpc(ROLE_ADOPTION_PREVIEW_RPC, { p_source_role_id: sourceRoleId });
  if (error) throw new RoleAdoptionError(error);
  const p = parseAdoptionPreview(data);
  if (!p) throw unreadable('The preview');
  return p;
}

/** `preview` must be the one the admin was shown: its count and dropped list are the confirmation. */
export async function adoptRole(preview: RoleAdoptionPreview, name: string | null, description: string | null): Promise<AdoptRoleResult> {
  const { data, error } = await supabase.rpc(ADOPT_ROLE_RPC, adoptRoleArgs(preview, name, description));
  if (error) throw new RoleAdoptionError(error);
  const r = parseAdoptResult(data);
  if (!r) throw unreadable('The adoption');
  return r;
}

export async function fetchRoleAdoptionStatus(roleId: number): Promise<RoleAdoptionStatus> {
  const { data, error } = await supabase.rpc(ROLE_ADOPTION_STATUS_RPC, { p_role_id: roleId });
  if (error) throw new RoleAdoptionError(error, 'Whether this role can be reversed could not be read.');
  const s = parseAdoptionStatus(data);
  if (!s) throw unreadable('The adoption status');
  return s;
}

export async function revertRoleAdoption(roleId: number): Promise<RevertAdoptionResult> {
  const { data, error } = await supabase.rpc(REVERT_ROLE_ADOPTION_RPC, { p_role_id: roleId });
  if (error) throw new RoleAdoptionError(error, 'The adoption could not be reversed.');
  const r = parseRevertResult(data);
  if (!r) throw unreadable('The reversal');
  return r;
}
